/**
 * The radar scope.
 *
 * This is the signature element: the product's core object is a pin plus a
 * radius, so rather than draw a map tile and overlay a circle, the radius
 * editor *is* a scope. Range rings are labelled in real distance, deals in
 * range appear as blips at their true bearing, and a sweep line runs to say
 * the thing is live.
 *
 * It is deliberately not a map. A map would invite panning and zooming, which
 * are not the decisions being made here — the only decisions are "where" and
 * "how far", and a scope puts both in one gesture.
 */

interface Blip {
  /** Metres from the pin. */
  distanceM: number;
  /** Degrees clockwise from north. */
  bearing: number;
  urgent?: boolean;
}

interface ScopeProps {
  radiusM: number;
  /** Outer ring distance. Defaults to 2x the radius so there's context around it. */
  scaleM?: number;
  blips?: Blip[];
  size?: number;
  sweep?: boolean;
  showLabels?: boolean;
  unit?: 'imperial' | 'metric';
}

function rangeLabel(meters: number, unit: 'imperial' | 'metric'): string {
  if (unit === 'imperial') {
    const mi = meters / 1609.344;
    return mi < 1 ? `${Math.round(mi * 10) / 10}mi` : `${Math.round(mi)}mi`;
  }
  const km = meters / 1000;
  return km < 1 ? `${Math.round(meters)}m` : `${Math.round(km)}km`;
}

export function Scope({
  radiusM,
  scaleM,
  blips = [],
  size = 260,
  sweep = true,
  showLabels = true,
  unit = 'imperial',
}: ScopeProps) {
  const scale = scaleM ?? radiusM * 2;
  const c = size / 2;
  const maxR = c - 12;
  const toPx = (meters: number) => Math.min(maxR, (meters / scale) * maxR);

  // Four evenly spaced range rings — enough to read distance off, few enough
  // to stay quiet behind the content.
  const rings = [0.25, 0.5, 0.75, 1].map((f) => ({ f, r: maxR * f, m: scale * f }));

  return (
    <svg
      className="scope"
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      role="img"
      aria-label={`Radar scope, ${rangeLabel(radiusM, unit)} radius, ${blips.length} deals in range`}
    >
      <defs>
        <radialGradient id="scope-glow">
          <stop offset="0%" stopColor="rgba(111,219,164,0.20)" />
          <stop offset="100%" stopColor="rgba(111,219,164,0)" />
        </radialGradient>
        {/* The sweep is a wedge fading to nothing, like a real PPI display. */}
        <linearGradient id="scope-sweep-fade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="rgba(111,219,164,0.34)" />
          <stop offset="100%" stopColor="rgba(111,219,164,0)" />
        </linearGradient>
      </defs>

      <circle cx={c} cy={c} r={maxR} fill="url(#scope-glow)" opacity="0.5" />

      {rings.map((ring) => (
        <g key={ring.f}>
          <circle className="scope-ring" cx={c} cy={c} r={ring.r} />
          {showLabels && (
            <text className="scope-ring-label" x={c + 3} y={c - ring.r + 9}>
              {rangeLabel(ring.m, unit)}
            </text>
          )}
        </g>
      ))}

      <line className="scope-crosshair" x1={c} y1={c - maxR} x2={c} y2={c + maxR} />
      <line className="scope-crosshair" x1={c - maxR} y1={c} x2={c + maxR} y2={c} />

      {/* The selected radius — the one thing the user is actually setting. */}
      <circle className="scope-radius" cx={c} cy={c} r={toPx(radiusM)} />

      {sweep && (
        <g className="scope-sweep">
          <path
            d={`M ${c} ${c} L ${c} ${c - maxR} A ${maxR} ${maxR} 0 0 1 ${
              c + maxR * Math.sin(Math.PI / 4)
            } ${c - maxR * Math.cos(Math.PI / 4)} Z`}
            fill="url(#scope-sweep-fade)"
          />
          <line x1={c} y1={c} x2={c} y2={c - maxR} stroke="rgba(111,219,164,0.5)" strokeWidth="1" />
        </g>
      )}

      {blips.map((b, i) => {
        const r = toPx(b.distanceM);
        const rad = ((b.bearing - 90) * Math.PI) / 180;
        return (
          <circle
            key={i}
            className={b.urgent ? 'scope-blip' : 'scope-pin'}
            cx={c + r * Math.cos(rad)}
            cy={c + r * Math.sin(rad)}
            r={b.distanceM <= radiusM ? 3.5 : 2}
            opacity={b.distanceM <= radiusM ? 1 : 0.35}
          />
        );
      })}

      {/* The pin itself. */}
      <circle className="scope-pin" cx={c} cy={c} r={4} />
      <circle cx={c} cy={c} r={7.5} fill="none" stroke="var(--price)" strokeWidth="1" opacity="0.5" />
    </svg>
  );
}

/** Tiny non-interactive version for the radar list in the rail. */
export function ScopeDial({ radiusM, active }: { radiusM: number; active: boolean }) {
  return (
    <div className="radar-dial" aria-hidden="true">
      <Scope
        radiusM={radiusM}
        scaleM={radiusM * 1.6}
        size={34}
        sweep={active}
        showLabels={false}
      />
    </div>
  );
}
